import axios from 'axios';
import { ENV } from '../../config/env';

const API_BASE_URL = ENV.API_BASE_URL;

export interface PlatformStats {
    candidates: number;
    companies: number;
    recruiters: number;
    admins: number;
    totalUsers: number;
}

export interface CreateAdminRequest {
    email: string;
    firstName: string;
    lastName: string;
    // Password removed - user will set via email link
}

export interface CreateAdminResponse {
    message: string;
    userId: string;
}

export interface PendingReview {
    _id: string;
    email: string;
    first_name?: string;
    last_name?: string;
    name?: string;
    type: 'candidate' | 'company';
    status: string;
    created_at: string;
    otp_verified_at?: string;
}

export interface RecentActivity {
    _id: string;
    user: string;
    action: string;
    timestamp: string;
    type: 'candidate' | 'company' | 'recruiter' | 'job';
}

class AdminApiService {
    private api;

    constructor() {
        this.api = axios.create({
            baseURL: `${API_BASE_URL}/admin/v1`,
            headers: {
                'Content-Type': 'application/json',
            },
        });

        // Add request interceptor to include auth token
        this.api.interceptors.request.use(
            (config) => {
                const token = localStorage.getItem('authToken');
                if (token) {
                    config.headers.Authorization = `Bearer ${token}`;
                }
                return config;
            },
            (error) => {
                return Promise.reject(error);
            }
        );
    }

    /**
     * Get platform statistics
     */
    async getStats(): Promise<PlatformStats> {
        try {
            const response = await this.api.get('/stats');
            return response.data.payload;
        } catch (error: any) {
            console.error('Failed to fetch platform stats:', error);
            throw error.response?.data || error;
        }
    }

    /**
     * Create a new admin user
     */
    async createAdmin(data: CreateAdminRequest): Promise<CreateAdminResponse> {
        try {
            const response = await this.api.post('/admin/create', data);
            return response.data.payload;
        } catch (error: any) {
            console.error('Failed to create admin:', error);
            throw error.response?.data || error;
        }
    }

    /**
     * Get pending reviews (candidates waiting for approval)
     */
    async getPendingReviews(): Promise<PendingReview[]> {
        try {
            // Call candidate-service application API - correct endpoint is /application/v1/pending
            const token = localStorage.getItem('authToken');
            const response = await axios.get(`${ENV.CANDIDATE_SERVICE_URL}/application/v1/pending`, {
                headers: token ? { Authorization: `Bearer ${token}` } : {},
            });
            const applications = response.data.payload?.data || [];

            // Transform to PendingReview format
            return applications.map((app: any) => ({
                _id: app.id,
                email: app.email,
                first_name: app.firstName,
                last_name: app.lastName,
                name: app.firstName && app.lastName
                    ? `${app.firstName} ${app.lastName}`
                    : app.email,
                type: 'candidate' as const,
                status: app.status,
                created_at: app.createdAt,
                otp_verified_at: app.otpVerifiedAt
            }));
        } catch (error: any) {
            console.error('Failed to fetch pending reviews:', error);
            return []; // Return empty array on error
        }
    }

    /**
     * Get recent platform activity
     */
    async getRecentActivity(): Promise<RecentActivity[]> {
        try {
            const response = await this.api.get('/activity');
            return response.data.payload || [];
        } catch (error: any) {
            console.error('Failed to fetch recent activity:', error);
            return []; // Return empty array on error
        }
    }
}

export const adminApiService = new AdminApiService();
